// Bounds-checked little-endian reader (protocol §2). Every malformed input throws ProtocolError,
// which the server turns into close 4004 (protocol §9.2) and the client treats as a decode error.

/** Thrown for any message that does not match the protocol (truncated, oversized, bad values). */
export class ProtocolError extends Error {
  override readonly name = 'ProtocolError';
  /** Byte offset in the message where the problem was found, when known. */
  readonly offset: number;

  constructor(message: string, offset = -1) {
    super(offset >= 0 ? `${message} (at byte ${offset})` : message);
    this.offset = offset;
  }
}

// Typed structurally: `TextDecoder` is a global *value* in every runtime, but Node's type
// definitions do not declare a global `TextDecoder` type.
let utf8Decoder: { decode(input: Uint8Array): string } | undefined;

/** Decodes UTF-8, replacing invalid sequences with U+FFFD (protocol §2). Keeps a leading BOM. */
export function decodeUtf8(bytes: Uint8Array): string {
  let ascii = true;
  for (let i = 0; i < bytes.length; i++) {
    if ((bytes[i] as number) >= 0x80) {
      ascii = false;
      break;
    }
  }
  if (ascii) {
    let s = '';
    for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i] as number);
    return s;
  }
  utf8Decoder ??= new TextDecoder('utf-8', { fatal: false, ignoreBOM: true });
  return utf8Decoder.decode(bytes);
}

export class Reader {
  private readonly buf: Uint8Array;
  private readonly dv: DataView;
  private cur: number;
  private readonly limit: number;

  /**
   * @param data the message bytes.
   * @param start first byte to read (default 0).
   * @param end one past the last byte this reader may read (default: the end of `data`).
   */
  constructor(data: Uint8Array | ArrayBufferLike, start = 0, end?: number) {
    this.buf = data instanceof Uint8Array ? data : new Uint8Array(data);
    this.dv = new DataView(this.buf.buffer, this.buf.byteOffset, this.buf.byteLength);
    const limit = end ?? this.buf.length;
    if (start < 0 || limit > this.buf.length || start > limit) {
      throw new RangeError(`Reader range [${start}, ${limit}) outside ${this.buf.length} bytes`);
    }
    this.cur = start;
    this.limit = limit;
  }

  /** Offset of the next byte to read. */
  get offset(): number {
    return this.cur;
  }

  /** One past the last readable byte. */
  get end(): number {
    return this.limit;
  }

  get remaining(): number {
    return this.limit - this.cur;
  }

  private need(n: number): void {
    if (this.cur + n > this.limit) {
      throw new ProtocolError(
        `unexpected end of data: need ${n} byte(s), have ${this.remaining}`,
        this.cur,
      );
    }
  }

  u8(): number {
    this.need(1);
    return this.buf[this.cur++] as number;
  }

  u16(): number {
    this.need(2);
    const v = this.dv.getUint16(this.cur, true);
    this.cur += 2;
    return v;
  }

  u32(): number {
    this.need(4);
    const v = this.dv.getUint32(this.cur, true);
    this.cur += 4;
    return v;
  }

  i8(): number {
    this.need(1);
    const v = this.dv.getInt8(this.cur);
    this.cur += 1;
    return v;
  }

  i16(): number {
    this.need(2);
    const v = this.dv.getInt16(this.cur, true);
    this.cur += 2;
    return v;
  }

  /** IEEE-754 single, which may be NaN or ±Infinity. Use `f32Finite` for protocol fields. */
  f32(): number {
    this.need(4);
    const v = this.dv.getFloat32(this.cur, true);
    this.cur += 4;
    return v;
  }

  /** IEEE-754 single that must be finite: NaN or ±Infinity is a protocol error (protocol §2). */
  f32Finite(): number {
    const at = this.cur;
    const v = this.f32();
    if (!Number.isFinite(v)) throw new ProtocolError(`non-finite f32: ${v}`, at);
    return v;
  }

  /**
   * Unsigned LEB128 varint (protocol §2): at most 5 bytes and 2³² − 1. A 6th byte or a larger
   * value is a protocol error.
   */
  vu(): number {
    const at = this.cur;
    let result = 0;
    for (let i = 0; i < 5; i++) {
      const b = this.u8();
      if (i < 4) {
        result |= (b & 0x7f) << (7 * i);
        if (b < 0x80) return result >>> 0;
      } else {
        if (b >= 0x80) throw new ProtocolError('varint longer than 5 bytes', at);
        if (b > 0x0f) throw new ProtocolError('varint larger than 2^32 - 1', at);
        return (result >>> 0) + b * 0x10000000;
      }
    }
    throw new ProtocolError('varint longer than 5 bytes', at); // unreachable
  }

  /**
   * `vl`: unsigned LEB128 varint up to 2⁵³ − 1 (protocol §2): at most 8 bytes. A 9th byte or a
   * value above `Number.MAX_SAFE_INTEGER` is a protocol error.
   */
  vl(): number {
    const at = this.cur;
    let result = 0;
    let scale = 1;
    for (let i = 0; i < 8; i++) {
      const b = this.u8();
      result += (b & 0x7f) * scale;
      if (b < 0x80) {
        if (result > Number.MAX_SAFE_INTEGER)
          throw new ProtocolError('vl larger than 2^53 - 1', at);
        return result;
      }
      scale *= 0x80;
    }
    throw new ProtocolError('vl longer than 8 bytes', at);
  }

  /**
   * `str`: a `vu` byte length, then UTF-8 (protocol §2). A length above `maxBytes` is a protocol
   * error; invalid UTF-8 is replaced with U+FFFD.
   */
  str(maxBytes: number): string {
    const at = this.cur;
    const n = this.vu();
    if (n > maxBytes) throw new ProtocolError(`string of ${n} bytes exceeds ${maxBytes}`, at);
    this.need(n);
    const s = decodeUtf8(this.buf.subarray(this.cur, this.cur + n));
    this.cur += n;
    return s;
  }

  /** `rgb`: three bytes red, green, blue, as a 0xRRGGBB number. */
  rgb(): number {
    this.need(3);
    const b = this.buf;
    const c =
      ((b[this.cur] as number) << 16) |
      ((b[this.cur + 1] as number) << 8) |
      (b[this.cur + 2] as number);
    this.cur += 3;
    return c;
  }

  /** `pos` in world units: `i16 × posQ`. */
  pos(posQ: number): number {
    return this.i16() * posQ;
  }

  /** `rad` in world units: `u16 × radQ`. */
  rad(radQ: number): number {
    return this.u16() * radQ;
  }

  /** The next `n` bytes as a view (no copy). */
  bytes(n: number): Uint8Array {
    this.need(n);
    const v = this.buf.subarray(this.cur, this.cur + n);
    this.cur += n;
    return v;
  }

  skip(n: number): void {
    this.need(n);
    this.cur += n;
  }

  /** A reader over the next `n` bytes; this reader moves past them. */
  sub(n: number): Reader {
    this.need(n);
    const r = new Reader(this.buf, this.cur, this.cur + n);
    this.cur += n;
    return r;
  }

  /** Throws if any bytes are left (protocol §9.2: the length must match the layout). */
  expectEnd(what = 'message'): void {
    if (this.cur !== this.limit) {
      throw new ProtocolError(
        `${this.limit - this.cur} unexpected trailing byte(s) in ${what}`,
        this.cur,
      );
    }
  }
}
